import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  CheckCircle, ChevronRight, ChevronDown, ChevronLeft,
  Lock, Menu, X, Award, ArrowRight, BookOpen,
} from 'lucide-react';
import ProtectedModule from '../components/ProtectedModule';
import { useModuleCompletion } from '../hooks/useModuleCompletion';
import { useModuleProgress } from '../hooks/useModuleProgress';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { useCourseAccess } from '../hooks/useCourseAccess';
import { PILLAR_ICON_URLS } from '../components/PillarIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAVY  = '#071B3A';
const IVORY = '#FAF8F3';
const GOLD  = '#C99316';

// ── Static module manifest ────────────────────────────────────────────────────
const MODULES_META = [
  { id: 1, title: 'Self-Anchoring',         lessonCount: 8, pillar: 'selfAnchoring',         free: true  },
  { id: 2, title: 'Perspective-Shifting',   lessonCount: 6, pillar: 'perspectiveShifting',   free: false },
  { id: 3, title: 'Adaptive Communication', lessonCount: 6, pillar: 'adaptiveCommunication', free: false },
  { id: 4, title: 'Relational Integration', lessonCount: 6, pillar: 'relationalIntegration', free: false },
];

// Build a flat lessons array from API content
const buildLessons = (content) => {
  const list = [];
  (content.sections || []).forEach(s =>
    list.push({ type: 'section', title: s.title, data: s })
  );
  if (content.exercise)
    list.push({ type: 'exercise', title: content.exercise.title || 'Practical Exercise', data: content.exercise });
  if (content.cfq_integration || content.module_close)
    list.push({
      type: 'finale',
      title: 'Reflection & Integration',
      data: {
        cfq:          content.cfq_integration,
        close:        content.module_close,
        quote:        content.reflection_quote,
        enhancements: content.enhancements,
      },
    });
  return list;
};

// ── Premium block renderers ───────────────────────────────────────────────────

const renderBlock = (block, i) => {
  switch (block.type) {

    case 'emphasis':
      return (
        <div
          key={i}
          className="flex gap-4 my-7 p-5 rounded-lg"
          style={{ backgroundColor: `${GOLD}10`, borderLeft: `3px solid ${GOLD}` }}
        >
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] font-sans font-semibold mb-1.5" style={{ color: GOLD }}>
              Key Idea
            </p>
            <p className="font-serif text-lg leading-[1.6]" style={{ color: NAVY }}>
              {block.text}
            </p>
          </div>
        </div>
      );

    case 'text':
      return (
        <div key={i}>
          {block.text.split('\n').map((line, j) =>
            line.trim() ? (
              <p key={j} className="font-sans font-light leading-[1.85] mb-4 text-[0.9375rem]" style={{ color: `${NAVY}CC` }}>
                {line}
              </p>
            ) : null
          )}
        </div>
      );

    case 'list':
      return (
        <ul key={i} className="space-y-2.5 my-5 ml-1">
          {block.items.map((item, j) => (
            <li key={j} className="flex items-start gap-3">
              <span className="mt-[6px] w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: GOLD }} />
              <span className="font-sans font-light leading-[1.75] text-[0.9375rem]" style={{ color: `${NAVY}CC` }}>
                {item}
              </span>
            </li>
          ))}
        </ul>
      );

    case 'quote_list':
      return (
        <div key={i} className="my-7 space-y-3">
          {block.items.map((item, j) => (
            <div
              key={j}
              className="pl-5 py-1"
              style={{ borderLeft: `2px solid ${GOLD}40` }}
            >
              <p className="font-serif italic leading-[1.65] text-[0.9375rem]" style={{ color: `${NAVY}99` }}>
                "{item}"
              </p>
            </div>
          ))}
        </div>
      );

    case 'cards':
      return (
        <div key={i} className="my-7 space-y-4">
          {block.cards.map((card, j) => (
            <div
              key={j}
              className="p-5 rounded-lg"
              style={{ backgroundColor: `${NAVY}06`, borderLeft: `3px solid ${NAVY}20` }}
            >
              <p className="text-[9px] uppercase tracking-[0.22em] font-sans font-semibold mb-2" style={{ color: GOLD }}>
                Scenario
              </p>
              <h4 className="font-serif text-base font-medium mb-2" style={{ color: NAVY }}>{card.title}</h4>
              <p className="font-sans font-light leading-[1.8] text-sm" style={{ color: `${NAVY}99` }}>{card.body}</p>
            </div>
          ))}
        </div>
      );

    case 'blindspot_cards':
      return (
        <div key={i} className="my-7 space-y-3" style={{ backgroundColor: `${NAVY}04`, borderRadius: '10px', padding: '1.25rem' }}>
          {block.cards.map((card, j) => (
            <div
              key={j}
              className="p-4 rounded-lg bg-white"
              style={{ borderLeft: `3px solid ${NAVY}` }}
            >
              <h4 className="font-serif text-sm font-semibold mb-1" style={{ color: NAVY }}>{card.title}</h4>
              <p className="font-sans font-light text-sm leading-[1.75]" style={{ color: `${NAVY}80` }}>
                <span className="font-medium" style={{ color: `${NAVY}99` }}>Blind spot: </span>{card.text}
              </p>
            </div>
          ))}
        </div>
      );

    case 'diagram':
    case 'comparison':
    case 'flow':
    case 'voice_grid': {
      const imgSrc = block.image;
      if (!imgSrc) return null;
      return (
        <div key={i} className="my-8">
          <img
            src={imgSrc}
            alt={block.alt || ''}
            className="w-full max-w-xl mx-auto rounded-lg"
            style={{ display: 'block', border: `1px solid ${NAVY}0D` }}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
          {block.caption && (
            <p className="text-center text-xs mt-2 italic" style={{ color: `${NAVY}4D` }}>{block.caption}</p>
          )}
        </div>
      );
    }

    default:
      return null;
  }
};

// ── Lesson content variants ───────────────────────────────────────────────────

const SectionLesson = ({ lesson, moduleContent }) => {
  const { data: section } = lesson;
  return (
    <div className="prose max-w-none">
      {section.blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
};

const ExerciseLesson = ({ lesson }) => {
  const ex = lesson.data;
  return (
    <div>
      {ex.intro && (
        <p className="font-sans font-light leading-[1.85] mb-7 text-[0.9375rem]" style={{ color: `${NAVY}CC` }}>
          {ex.intro}
        </p>
      )}
      <div className="space-y-5">
        {(ex.steps || []).map((step, i) => (
          <div
            key={i}
            className="p-5 rounded-lg"
            style={{ backgroundColor: `${NAVY}05`, border: `1px solid ${NAVY}0D` }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-sans font-semibold flex-shrink-0"
                style={{ backgroundColor: GOLD, color: NAVY }}
              >
                {i + 1}
              </div>
              <h4 className="font-serif text-base font-medium" style={{ color: NAVY }}>{step.label}</h4>
            </div>
            <p className="font-sans font-light text-sm leading-[1.8] pl-10" style={{ color: `${NAVY}99` }}>
              {step.text}
            </p>
          </div>
        ))}
      </div>
      {ex.closing && (
        <p className="mt-7 font-sans font-light leading-[1.85] text-[0.9375rem]" style={{ color: `${NAVY}CC` }}>
          {ex.closing}
        </p>
      )}
    </div>
  );
};

const FinaleLesson = ({ lesson, moduleContent, moduleNumber, isLoggedIn, completions, markComplete, completionLoading }) => {
  const { cfq, close, quote, enhancements } = lesson.data;
  const [marking, setMarking] = useState(false);
  const [marked, setMarked] = useState(false);
  const isCompleted = completions.some(c => c.module_number === moduleNumber);
  const navigate = useNavigate();

  const handleMark = async () => {
    setMarking(true);
    try {
      await markComplete(moduleNumber);
      setMarked(true);
    } catch (e) { console.error(e); }
    setMarking(false);
  };

  return (
    <div>
      {/* CFQ Integration */}
      {cfq && (
        <div className="mb-8">
          <div
            className="p-6 rounded-lg mb-6"
            style={{ backgroundColor: `${GOLD}0D`, borderLeft: `3px solid ${GOLD}` }}
          >
            <p className="text-[9px] uppercase tracking-[0.25em] font-sans font-semibold mb-3" style={{ color: GOLD }}>
              Your CFQ™ Result
            </p>
            <p className="font-sans font-light leading-[1.85] text-sm" style={{ color: `${NAVY}CC` }}>{cfq.intro}</p>
            {cfq.sub_intro && (
              <p className="mt-3 font-sans font-light leading-[1.85] text-sm" style={{ color: `${NAVY}CC` }}>{cfq.sub_intro}</p>
            )}
            <ul className="mt-4 space-y-2">
              {(cfq.questions || []).map((q, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-[6px] w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: GOLD }} />
                  <span className="font-sans font-light text-sm leading-[1.75]" style={{ color: `${NAVY}CC` }}>{q}</span>
                </li>
              ))}
            </ul>
            {cfq.closing && (
              <p className="mt-4 font-sans font-light text-sm leading-[1.85]" style={{ color: `${NAVY}CC` }}>{cfq.closing}</p>
            )}
          </div>
        </div>
      )}

      {/* Reflection Quote */}
      {quote && (
        <blockquote
          className="my-8 p-6 rounded-lg"
          style={{ backgroundColor: NAVY, borderLeft: `3px solid ${GOLD}` }}
        >
          <p className="font-serif text-lg italic leading-[1.7]" style={{ color: `${IVORY}CC` }}>
            "{quote}"
          </p>
          <p className="mt-3 text-[10px] uppercase tracking-[0.2em] font-sans" style={{ color: `${GOLD}80` }}>
            Module Reflection
          </p>
        </blockquote>
      )}

      {/* Enhancements */}
      {enhancements?.pause_and_reflect && (
        <div
          className="my-8 p-5 rounded-lg"
          style={{ backgroundColor: `${NAVY}06`, border: `1px solid ${NAVY}0D` }}
        >
          <p className="text-[9px] uppercase tracking-[0.25em] font-sans font-semibold mb-4" style={{ color: GOLD }}>
            Reflect
          </p>
          <ul className="space-y-3">
            {enhancements.pause_and_reflect.map((q, i) => (
              <li key={i} className="font-sans font-light text-[0.9375rem] leading-[1.75]" style={{ color: `${NAVY}CC` }}>
                {q}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Module Completion */}
      {isLoggedIn && (
        <div className="mt-10 pt-8" style={{ borderTop: `1px solid ${NAVY}10` }}>
          {isCompleted || marked ? (
            <div
              className="p-6 rounded-lg flex items-start gap-4"
              style={{ backgroundColor: `${GOLD}10`, border: `1px solid ${GOLD}30` }}
            >
              <CheckCircle size={20} style={{ color: GOLD, flexShrink: 0, marginTop: 2 }} />
              <div>
                <p className="font-serif text-base font-medium mb-1" style={{ color: NAVY }}>
                  Module Complete
                </p>
                <p className="text-sm font-sans font-light" style={{ color: `${NAVY}80` }}>
                  {completions.find(c => c.module_number === moduleNumber)?.completed_at
                    ? `Completed ${new Date(completions.find(c => c.module_number === moduleNumber).completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`
                    : 'Well done.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="font-sans font-light text-sm mb-5" style={{ color: `${NAVY}60` }}>
                You have reached the end of this module.
              </p>
              <button
                onClick={handleMark}
                disabled={marking || completionLoading}
                data-testid="mark-complete-btn"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 font-sans font-semibold rounded-lg transition-all text-sm"
                style={{ backgroundColor: GOLD, color: NAVY, opacity: (marking || completionLoading) ? 0.6 : 1 }}
              >
                {marking
                  ? <><div className="w-4 h-4 border-2 border-navy/30 border-t-navy rounded-full animate-spin" /> Marking Complete…</>
                  : <><CheckCircle size={16} /> Mark Module Complete</>}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Module Close */}
      {close && !close.is_final && (
        <div className="mt-8 text-center">
          <Link
            to={`/module-${close.next_module}`}
            data-testid={`proceed-module-${close.next_module}-btn`}
            className="inline-flex items-center gap-2.5 px-8 py-3.5 font-sans font-semibold rounded-lg text-sm"
            style={{ backgroundColor: NAVY, color: IVORY }}
          >
            Continue to Module {close.next_module} <ArrowRight size={15} />
          </Link>
        </div>
      )}

      {close?.is_final && (
        <div className="mt-8 text-center">
          <p className="font-serif text-sm mb-4" style={{ color: `${NAVY}60` }}>
            You have completed the full Third Culture Leadership programme.
          </p>
          <Link
            to="/courses"
            data-testid="complete-programme-btn"
            className="inline-flex items-center gap-2.5 px-8 py-3.5 font-sans font-semibold rounded-lg text-sm"
            style={{ backgroundColor: GOLD, color: NAVY }}
          >
            <Award size={16} /> Return to Dashboard
          </Link>
        </div>
      )}
    </div>
  );
};

// ── Course Sidebar ────────────────────────────────────────────────────────────

const CourseSidebar = ({
  moduleNumber, lessons, currentIdx, completedLessons,
  completions, isModuleAccessible, onSelectLesson, onClose,
}) => {
  const [expandedModule, setExpandedModule] = useState(moduleNumber);

  const moduleCompletion = (id) => completions.some(c => c.module_number === id);

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: NAVY }}>
      {/* Close button (mobile only) */}
      {onClose && (
        <div className="flex justify-end px-4 pt-4 lg:hidden">
          <button onClick={onClose} className="p-1.5 rounded" style={{ color: `${IVORY}60` }}>
            <X size={18} />
          </button>
        </div>
      )}

      {/* Course header */}
      <div className="px-5 pt-6 pb-4" style={{ borderBottom: `1px solid ${IVORY}10` }}>
        <p className="text-[9px] uppercase tracking-[0.28em] font-sans mb-1.5" style={{ color: `${GOLD}80` }}>
          Cultural Fluency Mini-Course
        </p>
        <Link to="/courses" className="block">
          <p className="font-serif text-sm leading-[1.4]" style={{ color: `${IVORY}CC` }}>
            Third Culture Leadership
          </p>
        </Link>
      </div>

      {/* Overall progress */}
      <div className="px-5 py-4" style={{ borderBottom: `1px solid ${IVORY}08` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] uppercase tracking-[0.22em] font-sans" style={{ color: `${IVORY}40` }}>
            Progress
          </span>
          <span className="text-[10px] font-sans font-medium" style={{ color: `${GOLD}CC` }}>
            {completions.length} of 4 modules
          </span>
        </div>
        <div className="h-0.5 rounded-full overflow-hidden" style={{ backgroundColor: `${IVORY}12` }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${(completions.length / 4) * 100}%`, backgroundColor: GOLD }}
          />
        </div>
      </div>

      {/* Module list */}
      <nav className="flex-1 overflow-y-auto py-2">
        {MODULES_META.map((mod) => {
          const accessible = mod.free || isModuleAccessible(mod.id);
          const isActive   = mod.id === moduleNumber;
          const isDone     = moduleCompletion(mod.id);
          const isExpanded = expandedModule === mod.id;

          return (
            <div key={mod.id}>
              {/* Module header row */}
              <button
                onClick={() => {
                  if (accessible) {
                    setExpandedModule(isExpanded ? null : mod.id);
                    if (!isActive) window.location.href = `/module-${mod.id}`;
                  }
                }}
                className="w-full flex items-center gap-2.5 px-5 py-3 text-left transition-colors"
                style={{
                  backgroundColor: isActive ? `${IVORY}08` : 'transparent',
                }}
              >
                <img
                  src={PILLAR_ICON_URLS[mod.pillar]}
                  alt=""
                  width={20}
                  height={20}
                  style={{ opacity: accessible ? 0.7 : 0.25, objectFit: 'contain', flexShrink: 0 }}
                />
                <div className="flex-1 min-w-0">
                  <span className="text-[9px] uppercase tracking-[0.2em] font-sans block mb-0.5" style={{ color: `${IVORY}30` }}>
                    Module {String(mod.id).padStart(2, '0')}
                  </span>
                  <span
                    className="font-sans text-xs font-medium truncate block"
                    style={{ color: isActive ? IVORY : accessible ? `${IVORY}70` : `${IVORY}30` }}
                  >
                    {mod.title}
                  </span>
                </div>
                <div className="flex-shrink-0 ml-auto">
                  {!accessible
                    ? <Lock size={11} style={{ color: `${IVORY}25` }} />
                    : isDone
                      ? <CheckCircle size={11} style={{ color: GOLD }} />
                      : isExpanded
                        ? <ChevronDown size={11} style={{ color: `${IVORY}40` }} />
                        : <ChevronRight size={11} style={{ color: `${IVORY}30` }} />}
                </div>
              </button>

              {/* Lesson list (current module only) */}
              {isActive && isExpanded && (
                <div className="pb-1" style={{ backgroundColor: `${IVORY}04` }}>
                  {lessons.map((lesson, idx) => {
                    const done    = completedLessons.has(idx);
                    const current = idx === currentIdx;
                    return (
                      <button
                        key={idx}
                        onClick={() => onSelectLesson(idx)}
                        data-testid={`sidebar-lesson-${idx + 1}`}
                        className="w-full flex items-start gap-2.5 px-5 pl-10 py-2 text-left transition-colors"
                        style={{
                          borderLeft: current ? `2px solid ${GOLD}` : '2px solid transparent',
                          backgroundColor: current ? `${GOLD}10` : 'transparent',
                        }}
                      >
                        <div className="flex-shrink-0 mt-[3px]">
                          {done
                            ? <CheckCircle size={10} style={{ color: GOLD }} />
                            : current
                              ? <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GOLD }} />
                              : <div className="w-2 h-2 rounded-full border" style={{ borderColor: `${IVORY}30` }} />}
                        </div>
                        <span
                          className="font-sans text-[11px] leading-[1.5] flex-1"
                          style={{ color: current ? IVORY : done ? `${IVORY}60` : `${IVORY}50` }}
                        >
                          {lesson.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

// ── Main module viewer ────────────────────────────────────────────────────────

const ModuleViewerContent = ({ moduleNumber }) => {
  const auth = useReaderAuth();
  const isLoggedIn  = auth?.isAuthenticated?.() ?? false;
  const token       = auth?.token;

  const { completions = [], isLoading: completionLoading, markComplete } = useModuleCompletion();
  const { saveProgress } = useModuleProgress();
  const { hasAccess, purchasedModules, accessType } = useCourseAccess();

  const [content,      setContent]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [currentIdx,   setCurrentIdx]   = useState(0);
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [completedLessons, setCompletedLessons] = useState(new Set());

  const lessons = content ? buildLessons(content) : [];
  const totalLessons  = lessons.length;
  const currentLesson = lessons[currentIdx];
  const isFirstLesson = currentIdx === 0;
  const isLastLesson  = currentIdx === totalLessons - 1;

  const isModuleAccessible = useCallback((id) => {
    if (id === 1) return true;
    if (accessType === 'whitelisted' || hasAccess) return true;
    return (purchasedModules || []).includes(`module-${id}`);
  }, [hasAccess, purchasedModules, accessType]);

  // Fetch module content
  useEffect(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    setLoading(true);
    fetch(`${API_URL}/api/modules/content/${moduleNumber}`, { headers })
      .then(r => { if (!r.ok) throw new Error('Failed to load module'); return r.json(); })
      .then(data => setContent(data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [moduleNumber, token]);

  // Mark visited lessons as "completed locally" (for sidebar state)
  useEffect(() => {
    setCompletedLessons(prev => {
      if (prev.has(currentIdx)) return prev;
      return new Set([...prev, currentIdx]);
    });
  }, [currentIdx]);

  // Save progress when lesson changes
  useEffect(() => {
    if (isLoggedIn && totalLessons > 0) {
      saveProgress(moduleNumber, currentIdx + 1, totalLessons);
    }
  }, [currentIdx, isLoggedIn, moduleNumber, totalLessons, saveProgress]);

  // Scroll to top on lesson change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentIdx]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: IVORY }}>
        <div className="text-center">
          <div
            className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-4"
            style={{ borderColor: `${GOLD}40`, borderTopColor: GOLD }}
          />
          <p className="font-sans text-sm" style={{ color: `${NAVY}50` }}>Loading module…</p>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: IVORY }}>
        <div className="text-center max-w-sm px-6">
          <p className="font-serif text-2xl mb-3" style={{ color: NAVY }}>Module unavailable</p>
          <p className="font-sans font-light text-sm" style={{ color: `${NAVY}60` }}>
            {error || 'Unable to load content.'}
          </p>
          <Link to="/courses" className="mt-6 inline-block text-sm font-sans" style={{ color: GOLD }}>
            ← Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const modMeta = MODULES_META.find(m => m.id === moduleNumber);

  return (
    <div className="flex" style={{ minHeight: 'calc(100vh - 70px)', backgroundColor: IVORY }}>

      {/* ── Desktop sidebar (fixed) ── */}
      <aside
        className="hidden lg:flex flex-col fixed left-0 w-72 overflow-y-auto z-20"
        style={{ top: 70, bottom: 0 }}
        data-testid="course-sidebar"
      >
        <CourseSidebar
          moduleNumber={moduleNumber}
          lessons={lessons}
          currentIdx={currentIdx}
          completedLessons={completedLessons}
          completions={completions}
          isModuleAccessible={isModuleAccessible}
          onSelectLesson={setCurrentIdx}
        />
      </aside>

      {/* ── Mobile sidebar drawer ── */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <aside
            className="fixed top-0 left-0 w-[300px] h-full z-50 flex flex-col lg:hidden"
            style={{ backgroundColor: NAVY }}
            data-testid="mobile-sidebar"
          >
            <CourseSidebar
              moduleNumber={moduleNumber}
              lessons={lessons}
              currentIdx={currentIdx}
              completedLessons={completedLessons}
              completions={completions}
              isModuleAccessible={isModuleAccessible}
              onSelectLesson={(i) => { setCurrentIdx(i); setSidebarOpen(false); }}
              onClose={() => setSidebarOpen(false)}
            />
          </aside>
        </>
      )}

      {/* ── Main content area ── */}
      <main className="flex-1 lg:ml-72 flex flex-col min-h-[calc(100vh-70px)]">

        {/* Sticky lesson header */}
        <header
          className="sticky top-[70px] z-10 px-6 lg:px-10 py-4"
          style={{ backgroundColor: IVORY, borderBottom: `1px solid ${NAVY}0D` }}
          data-testid="lesson-header"
        >
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
            {/* Mobile: hamburger + course name */}
            <button
              className="lg:hidden flex items-center gap-2.5 text-sm font-sans"
              style={{ color: `${NAVY}80` }}
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-btn"
              aria-label="Open course navigation"
            >
              <Menu size={18} />
              <span className="text-[11px] uppercase tracking-[0.2em]">Contents</span>
            </button>

            {/* Breadcrumb */}
            <div className="hidden lg:flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-sans" style={{ color: `${NAVY}40` }}>
              <span style={{ color: GOLD }}>{modMeta?.title}</span>
              <ChevronRight size={10} />
              <span>Lesson {currentIdx + 1} of {totalLessons}</span>
            </div>

            {/* Lesson progress pill */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2">
                <div
                  className="h-1 w-24 rounded-full overflow-hidden"
                  style={{ backgroundColor: `${NAVY}10` }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${((currentIdx + 1) / totalLessons) * 100}%`, backgroundColor: GOLD }}
                  />
                </div>
                <span className="text-[10px] font-sans font-medium" style={{ color: `${NAVY}50` }}>
                  {Math.round(((currentIdx + 1) / totalLessons) * 100)}%
                </span>
              </div>

              {/* Mobile lesson indicator */}
              <span className="lg:hidden text-[10px] font-sans" style={{ color: `${NAVY}50` }}>
                {currentIdx + 1}/{totalLessons}
              </span>
            </div>
          </div>
        </header>

        {/* Lesson body */}
        <div className="flex-1 px-6 lg:px-10 py-10">
          <div className="max-w-3xl mx-auto">

            {/* Lesson title block */}
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[9px] uppercase tracking-[0.3em] font-sans font-semibold"
                  style={{ color: `${GOLD}CC` }}
                >
                  Module {String(moduleNumber).padStart(2, '0')} · Lesson {currentIdx + 1}
                </span>
                {currentLesson?.type === 'exercise' && (
                  <span
                    className="text-[8px] uppercase tracking-[0.2em] font-sans px-2 py-0.5 rounded"
                    style={{ backgroundColor: `${GOLD}15`, color: GOLD }}
                  >
                    Exercise
                  </span>
                )}
                {currentLesson?.type === 'finale' && (
                  <span
                    className="text-[8px] uppercase tracking-[0.2em] font-sans px-2 py-0.5 rounded"
                    style={{ backgroundColor: `${NAVY}0A`, color: `${NAVY}60` }}
                  >
                    Reflection
                  </span>
                )}
              </div>

              <h1
                className="font-serif font-light leading-[1.1] tracking-tight"
                style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: NAVY }}
                data-testid="lesson-title"
              >
                {currentLesson?.title}
              </h1>

              {/* Est. read time */}
              {currentLesson?.type === 'section' && (
                <div className="flex items-center gap-1.5 mt-3">
                  <BookOpen size={11} style={{ color: `${NAVY}35` }} />
                  <span className="text-[10px] font-sans" style={{ color: `${NAVY}35` }}>
                    {Math.max(2, Math.ceil((currentLesson.data.blocks || []).length * 0.4))} min read
                  </span>
                </div>
              )}

              <div className="mt-5 h-px" style={{ backgroundColor: `${NAVY}0C` }} />
            </div>

            {/* Lesson content */}
            <div data-testid={`lesson-content-${currentIdx}`}>
              {currentLesson?.type === 'section' && (
                <SectionLesson lesson={currentLesson} moduleContent={content} />
              )}
              {currentLesson?.type === 'exercise' && (
                <ExerciseLesson lesson={currentLesson} />
              )}
              {currentLesson?.type === 'finale' && (
                <FinaleLesson
                  lesson={currentLesson}
                  moduleContent={content}
                  moduleNumber={moduleNumber}
                  isLoggedIn={isLoggedIn}
                  completions={completions}
                  markComplete={markComplete}
                  completionLoading={completionLoading}
                />
              )}
            </div>

            {/* Bottom lesson navigation */}
            <div
              className="mt-12 pt-6 flex items-center justify-between gap-4"
              style={{ borderTop: `1px solid ${NAVY}0D` }}
              data-testid="lesson-navigation"
            >
              <button
                onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
                disabled={isFirstLesson}
                data-testid="prev-lesson-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-sans text-sm transition-all"
                style={{
                  border: `1px solid ${NAVY}15`,
                  color: isFirstLesson ? `${NAVY}25` : `${NAVY}70`,
                  cursor: isFirstLesson ? 'not-allowed' : 'pointer',
                }}
              >
                <ChevronLeft size={15} />
                <span className="hidden sm:inline">Previous</span>
              </button>

              <span className="font-sans text-[11px] text-center" style={{ color: `${NAVY}40` }}>
                Lesson {currentIdx + 1} of {totalLessons}
              </span>

              {isLastLesson ? (
                <span
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-sans text-sm"
                  style={{ backgroundColor: `${GOLD}15`, color: `${GOLD}` }}
                >
                  <CheckCircle size={14} />
                  Final Lesson
                </span>
              ) : (
                <button
                  onClick={() => setCurrentIdx(i => Math.min(totalLessons - 1, i + 1))}
                  data-testid="next-lesson-btn"
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-sans font-semibold text-sm transition-all"
                  style={{ backgroundColor: NAVY, color: IVORY }}
                >
                  <span className="hidden sm:inline">Next Lesson</span>
                  <span className="sm:hidden">Next</span>
                  <ChevronRight size={15} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sticky bottom mini-bar (mobile) */}
        <div
          className="lg:hidden sticky bottom-0 z-10 flex items-center justify-between px-5 py-3"
          style={{ backgroundColor: NAVY, borderTop: `1px solid ${IVORY}10` }}
          data-testid="sticky-bottom-nav"
        >
          <button
            onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
            disabled={isFirstLesson}
            className="flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.15em]"
            style={{ color: isFirstLesson ? `${IVORY}25` : `${IVORY}60` }}
          >
            <ChevronLeft size={13} /> Prev
          </button>
          <span className="font-sans text-[10px]" style={{ color: `${IVORY}40` }}>
            {currentIdx + 1} / {totalLessons}
          </span>
          <button
            onClick={() => setCurrentIdx(i => Math.min(totalLessons - 1, i + 1))}
            disabled={isLastLesson}
            className="flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.15em]"
            style={{ color: isLastLesson ? `${IVORY}25` : GOLD }}
          >
            Next <ChevronRight size={13} />
          </button>
        </div>
      </main>
    </div>
  );
};

// ── Public wrapper ────────────────────────────────────────────────────────────

const moduleNames = {
  1: 'Module 1: Self-Anchoring',
  2: 'Module 2: Perspective-Shifting',
  3: 'Module 3: Adaptive Communication',
  4: 'Module 4: Relational Integration',
};

const ModuleViewer = ({ moduleNumber }) => {
  const inner = <ModuleViewerContent moduleNumber={moduleNumber} />;
  if (moduleNumber === 1) return inner;
  return (
    <ProtectedModule moduleNumber={moduleNumber} moduleName={moduleNames[moduleNumber]}>
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      {inner}
    </ProtectedModule>
  );
};

export default ModuleViewer;
